import React from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { SearchFiltersValues } from "../../models/SearchFilters";

export interface SearchFiltersProps {
  values: SearchFiltersValues;
  handleChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  resetFilters: () => void;
}

export const SearchFilters = (props: SearchFiltersProps) => {
  const { values, handleChange, resetFilters } = props;
  const statuses = [
    "Active",
    "Sold",
    "Sold Cond",
    "Sold Cond Esc",
    "Suspended",
    "Terminated",
    "Expired",
  ];
  const squareFootRanges = [
    "< 700",
    "700-1100",
    "1100-1500",
    "1500-2000",
    "2000-2500",
    "2500-3000",
    "3000-3500",
    "3500-5000",
    "5000+",
  ];
  const propertyTypes = [
    "Attached/Row/Townhouse",
    "Cottage",
    "Detached",
    "Duplex",
    "Farm",
    "Fourplex",
    "Link",
    "Lower Level",
    "Mobile/Trailer",
    "Multiplex",
    "Other",
    "Room",
    "Rural Residence",
    "Semi-Detached",
    "Shared Room",
    "Store with Apt/Office",
    "Triplex",
    "Upper Level",
    "Vacant Land",
  ];
  const bedroomBathroomParkingOptions = ["1", "2", "3", "4", "5+"];
  const acOptions = ["Central Air", "Wall Unit", "Window Unit", "Other", "None"];
  const basementOptions = [
    "Apartment",
    "Crawl Space",
    "Finished",
    "Finished W/O",
    "Full",
    "Half",
    "None",
    "Other",
    "Partial Basement",
    "Partially Finished",
    "Separate Entrance",
    "Unfinished",
    "Walk Out",
  ];
  const kitchenOptions = ["1", "2", "3+"];

  const ageOptions = ["New", "0-5", "6-15", "16-30", "31-50", "51-99", "100+"];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flexGrow: 1, overflow: "auto", padding: "16px" }}>
        <Grid container spacing={2}>
          {/* Left side - Statuses */}
          <Grid item sm={6}>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={values.Lsc}
                    multiple
                    onChange={handleChange("Lsc")}
                    label="Lsc"
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                  >
                    {statuses.map((status, index) => (
                      <MenuItem key={index} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={6}>
                <TextField
                  label="Min Date Range"
                  variant="outlined"
                  fullWidth
                  value={values.minStatusDateRange}
                  onChange={handleChange("minStatusDateRange")}
                />
              </Grid>
              <Grid item sm={6}>
                <TextField
                  label="Max Date Range"
                  variant="outlined"
                  fullWidth
                  value={values.maxStatusDateRange}
                  onChange={handleChange("maxStatusDateRange")}
                />
              </Grid>
              <Grid item sm={12}>
                <FormControl fullWidth size="small" sx={{ marginBottom: "16px" }}>
                  <InputLabel>Property Type</InputLabel>
                  <Select
                    value={values.Type_own1_out}
                    multiple
                    onChange={handleChange("Type_own1_out")}
                    label="Property Type"
                  >
                    {propertyTypes.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={12}>
                <FormControl fullWidth size="small" sx={{ marginBottom: "16px" }}>
                  <InputLabel>Square Footage</InputLabel>
                  <Select
                    value={values.sqft}
                    multiple
                    onChange={handleChange("sqft")}
                    label="Square Footage"
                  >
                    {squareFootRanges.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>A/C</InputLabel>
                  <Select
                    value={values.A_c}
                    multiple
                    onChange={handleChange("A_c")}
                    label="A/C"
                  >
                    {acOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Approx. Age</InputLabel>
                  <Select
                    value={values.Yr_built}
                    multiple
                    onChange={handleChange("Yr_built")}
                    label="Approx. Age"
                  >
                    {ageOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Basement</InputLabel>
                  <Select
                    value={values.Bsmt1_out}
                    multiple
                    onChange={handleChange("Bsmt1_out")}
                    label="Basement"
                  >
                    {basementOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
  
          {/* Right side - Other Fields */}
          <Grid item sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bedrooms</InputLabel>
                  <Select
                    value={values.Br}
                    multiple
                    onChange={handleChange("Br")}
                    label="Br"
                  >
                    {bedroomBathroomParkingOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Min Price"
                  variant="outlined"
                  fullWidth
                  value={values.minPrice}
                  onChange={handleChange("minPrice")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Price"
                  variant="outlined"
                  fullWidth
                  value={values.maxPrice}
                  onChange={handleChange("maxPrice")}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Kitchens</InputLabel>
                  <Select
                    value={values.Num_kit}
                    multiple
                    onChange={handleChange("Num_kit")}
                    label="Kitchens"
                  >
                    {kitchenOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Garage Spaces</InputLabel>
                  <Select
                    value={values.Gar_spaces}
                    multiple
                    onChange={handleChange("Gar_spaces")}
                    label="Garage Spaces"
                  >
                    {bedroomBathroomParkingOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Driveway Spaces</InputLabel>
                  <Select
                    value={values.Park_spcs}
                    multiple
                    onChange={handleChange("Park_spcs")}
                    label="Driveway Spaces"
                  >
                    {bedroomBathroomParkingOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Total Parking Spaces</InputLabel>
                  <Select
                    value={values.Tot_park_spcs}
                    multiple
                    onChange={handleChange("Tot_park_spcs")}
                    label="Total Parking Spaces"
                  >
                    {bedroomBathroomParkingOptions.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
  
      <Box sx={{ padding: "16px", borderTop: "1px solid #ccc", position: "sticky", bottom: 0, backgroundColor: "#fff", zIndex: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={resetFilters}
          sx={{ marginRight: "8px", height: "56px" }} // Specify height in the sx object
          style={{ backgroundColor: '#3f51b5' }}
        >
          Reset
        </Button>
      </Box>



    </Box>
  );  
};